import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useRequireAuth, formatErr } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Trash2, UserPlus, Loader2, Users } from "lucide-react";

const ROLES = ["cliente", "operaio", "personale", "admin"];
const WORKER_CATS = ["elettricista", "idraulico", "frigorista"];
const WORKER_SUBS = ["apprendista", "operaio", "tecnico"];
const ROLE_STYLES = {
  cliente: "bg-slate-100 text-slate-800",
  operaio: "bg-purple-100 text-purple-900",
  personale: "bg-blue-100 text-blue-800",
  admin: "bg-amber-100 text-amber-900",
};

export default function AdminUsers() {
  const { user } = useRequireAuth("admin");
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [tempInfo, setTempInfo] = useState(null);

  const load = async () => {
    setBusy(true);
    try { const r = await api.get("/admin/users"); setUsers(r.data); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (user) load(); }, [user]);

  const changeRole = async (uid, role) => {
    try { await api.patch(`/admin/users/${uid}`, { role }); toast.success("Ruolo aggiornato"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const toggleMarketing = async (uid, value) => {
    try { await api.patch(`/admin/users/${uid}`, { marketing_opt_in: value }); toast.success(value ? "Iscritto alla mailing list" : "Rimosso dalla mailing list"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const setWorkerField = async (uid, patch) => {
    try { await api.patch(`/admin/users/${uid}`, patch); toast.success("Aggiornato"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const resetPwd = async (uid, email) => {
    const cc = window.prompt(`Reimpostare la password di ${email}?\n\nSe vuoi inviare le credenziali anche a un secondo destinatario, inserisci la sua email qui sotto (opzionale, lascia vuoto per saltare):`, "");
    if (cc === null) return; // user cancelled
    try {
      const body = { send_email: true };
      if (cc && cc.trim()) body.cc_email = cc.trim();
      const r = await api.post(`/admin/users/${uid}/reset-password`, body);
      const ed = r.data.email_delivery || {};
      if (ed.owner_sent) toast.success(`Nuova password inviata a ${email}${ed.cc_sent ? " + CC" : ""}`);
      else toast.warning("Password reimpostata ma email non consegnata — comunicala a mano");
      setTempInfo({ email, password: r.data.temporary_password, email_delivery: ed });
    } catch (e) { toast.error(formatErr(e)); }
  };
  const delUser = async (uid, email) => {
    if (!window.confirm(`Eliminare definitivamente l'utente ${email}?`)) return;
    try { await api.delete(`/admin/users/${uid}`); toast.success("Utente eliminato"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  if (!user) return <div className="min-h-screen"><Navbar/></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-6xl mx-auto p-6">
        <div className="mt-6 mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">Amministrazione</div>
            <h1 className="heading text-3xl md:text-4xl font-extrabold text-slate-900 flex items-center gap-2"><Users className="w-7 h-7"/> Gestione utenti</h1>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="bg-blue-800 hover:bg-blue-900" data-testid="admin-user-create-btn">
            <UserPlus className="w-4 h-4 mr-2"/> Nuovo utente
          </Button>
        </div>

        {busy ? <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-800 mx-auto"/></div> : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="text-left p-3">Nome</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">Ruolo</th>
                  <th className="text-left p-3">Telefono</th>
                  <th className="text-left p-3">Mailing / Operaio</th>
                  <th className="text-left p-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} className="border-t hover:bg-slate-50" data-testid={`admin-user-row-${u.user_id}`}>
                    <td className="p-3 font-medium">{u.name}</td>
                    <td className="p-3 text-slate-600">{u.email}</td>
                    <td className="p-3">
                      <Select value={u.role} onValueChange={v => changeRole(u.user_id, v)} disabled={u.user_id === user.user_id}>
                        <SelectTrigger className={`w-36 ${ROLE_STYLES[u.role] || ""}`} data-testid={`admin-user-role-${u.user_id}`}><SelectValue/></SelectTrigger>
                        <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-3 text-slate-600">{u.phone || "—"}</td>
                    <td className="p-3">
                      {u.role === "cliente" ? (
                        <Switch checked={!!u.marketing_opt_in} onCheckedChange={v => toggleMarketing(u.user_id, v)} data-testid={`admin-user-marketing-${u.user_id}`}/>
                      ) : u.role === "operaio" ? (
                        <div className="flex flex-col gap-1">
                          <div className="flex gap-1">
                            <Select value={u.worker_category || ""} onValueChange={v => setWorkerField(u.user_id, { worker_category: v })}>
                              <SelectTrigger className="w-32 h-8 text-xs" data-testid={`admin-user-wcat-${u.user_id}`}><SelectValue placeholder="cat."/></SelectTrigger>
                              <SelectContent>{WORKER_CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={u.worker_subcategory || ""} onValueChange={v => setWorkerField(u.user_id, { worker_subcategory: v })}>
                              <SelectTrigger className="w-32 h-8 text-xs" data-testid={`admin-user-wsub-${u.user_id}`}><SelectValue placeholder="sub."/></SelectTrigger>
                              <SelectContent>{WORKER_SUBS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <label className="flex items-center gap-1 text-xs">
                            <input type="checkbox" checked={!!u.is_responsible} onChange={e => setWorkerField(u.user_id, { is_responsible: e.target.checked })} data-testid={`admin-user-resp-${u.user_id}`}/>
                            Responsabile
                          </label>
                        </div>
                      ) : <span className="text-slate-400 text-xs">—</span>}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => resetPwd(u.user_id, u.email)} data-testid={`admin-user-reset-${u.user_id}`}><KeyRound className="w-4 h-4 mr-1"/>Reset</Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => delUser(u.user_id, u.email)} disabled={u.user_id === user.user_id} data-testid={`admin-user-del-${u.user_id}`}><Trash2 className="w-4 h-4"/></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={(info) => { setTempInfo(info); load(); }}/>
      <Dialog open={!!tempInfo} onOpenChange={(v) => !v && setTempInfo(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Password temporanea generata</DialogTitle>
            <DialogDescription>Comunica queste credenziali all'utente. Dovrà cambiare la password al primo accesso.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <div><span className="text-slate-500 text-xs">Email</span><div className="font-mono bg-slate-50 p-2 rounded">{tempInfo?.email}</div></div>
            <div><span className="text-slate-500 text-xs">Password temporanea</span><div className="font-mono bg-amber-50 border border-amber-200 p-2 rounded font-bold text-amber-900" data-testid="admin-temp-password">{tempInfo?.password}</div></div>
          </div>
          <Button onClick={() => setTempInfo(null)} className="mt-4 w-full bg-blue-800">Ho copiato</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateUserDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "personale", cc_email: "", send_email: true });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = {
        name: form.name, email: form.email, phone: form.phone, role: form.role,
        send_email: form.send_email,
      };
      if (form.cc_email.trim()) body.cc_email = form.cc_email.trim();
      const r = await api.post("/admin/users", body);
      onOpenChange(false);
      const ed = r.data.email_delivery || {};
      if (form.send_email) {
        if (ed.owner_sent) toast.success(`Credenziali inviate a ${r.data.email}${ed.cc_sent ? " + CC" : ""}`);
        else toast.warning("Credenziali salvate ma email non consegnata — comunicale a mano");
      }
      onCreated({ email: r.data.email, password: r.data.temporary_password, email_delivery: ed });
      setForm({ name: "", email: "", phone: "", role: "personale", cc_email: "", send_email: true });
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nuovo utente</DialogTitle>
          <DialogDescription>Verrà creata una password temporanea e inviata via email all'utente (con eventuale CC)</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="create-user-form">
          <Input placeholder="Nome e Cognome" required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="create-user-name"/>
          <Input type="email" placeholder="Email (destinatario principale)" required value={form.email} onChange={e=>setForm({...form,email:e.target.value})} data-testid="create-user-email"/>
          <Input placeholder="Telefono (opzionale)" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} data-testid="create-user-phone"/>
          <Select value={form.role} onValueChange={v => setForm({...form, role:v})}>
            <SelectTrigger data-testid="create-user-role"><SelectValue/></SelectTrigger>
            <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <div className="border-t pt-3 space-y-2">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="cu-send-email" checked={form.send_email} onChange={e=>setForm({...form,send_email:e.target.checked})}
                     className="rounded border-slate-300" data-testid="create-user-send-email"/>
              <label htmlFor="cu-send-email" className="text-sm text-slate-700 cursor-pointer">Invia credenziali via email</label>
            </div>
            {form.send_email && (
              <Input type="email" placeholder="CC (opzionale, es. responsabile@azienda.it)"
                     value={form.cc_email} onChange={e=>setForm({...form,cc_email:e.target.value})}
                     data-testid="create-user-cc-email"/>
            )}
          </div>
          <Button disabled={busy} className="w-full bg-blue-800" data-testid="create-user-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Crea utente"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
